<!--
集積所検索コンテナコンポーネント

機能:
- 管理番号による集積所検索
- 一致する地点を地図の中心に表示
-->
<template>
  <div>
    <StationSearchView
      :loading="isLoading || loading"
      @search="handleSearch"
    />
    <ToastMessage ref="toastRef" />
  </div>
</template>

<script setup>
import { ref, watch, onBeforeUnmount } from 'vue'
import StationSearchView from '../components/StationSearchView.vue'
import ToastMessage from '@/components/molecules/ToastMessage.vue'
import geocodingApi from '@/services/developerSettings/geocodingApi.js'
import maplibregl from 'maplibre-gl'

const props = defineProps({
  /**
   * 地図コンポーネントのインスタンス
   */
  mapInstance: {
    type: Object,
    default: null
  },
  /**
   * 地図上に表示されている集積所データ（配列またはGeoJSONオブジェクト）
   */
  stationsData: {
    type: [Array, Object],
    default: () => []
  },
  /**
   * ローディング状態（地図読み込み中など）
   */
  loading: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['focus-on-location', 'station-selected', 'search-performed'])

const isLoading = ref(false)
const addressSearchMarker = ref(null)
const toastRef = ref(null)

/**
 * 検索処理
 * 検索モードに応じて管理番号または住所で検索
 * @param {Object|string} queryOrObject - 検索クエリ（文字列または{ query, mode }オブジェクト）
 */
const handleSearch = async (queryOrObject) => {
  // 新形式（オブジェクト）または旧形式（文字列）に対応
  const query = typeof queryOrObject === 'string' ? queryOrObject : queryOrObject.query
  const mode = typeof queryOrObject === 'object' ? queryOrObject.mode : 'number'

  if (!query || query.trim() === '') {
    return
  }

  isLoading.value = true

  try {
    if (mode === 'address') {
      // 住所検索
      await searchByAddress(query.trim())
    } else {
      // 管理番号検索（既存の処理）
      await searchByNumber(query.trim())
    }
  } catch (error) {
    console.error('検索エラー:', error)
    showToast('検索中にエラーが発生しました', 'danger')
  } finally {
    isLoading.value = false
  }
}

/**
 * 管理番号で検索（既存の処理を切り出し）
 */
const searchByNumber = async (query) => {
  // stationsData から該当する集積所を検索
  const foundStation = findStationByNumber(query)

  if (foundStation) {
    // 見つかった場合は地図の中心に表示
    focusMapOnStation(foundStation)

    showToast(`管理番号「${foundStation.managementNumber}」の集積所が見つかりました。`, 'success')
  } else {
    // 見つからなかった場合
    showToast(`管理番号「${query}」に該当する集積所が見つかりませんでした。`, 'warning')
  }
}

/**
 * 住所で検索
 * 国土地理院APIを使用して緯度・経度を取得
 */
const searchByAddress = async (address) => {
  try {
    // 国土地理院APIを呼び出し
    const result = await geocodingApi.searchAddress(address)

    // 既存のマーカーを削除
    if (addressSearchMarker.value) {
      addressSearchMarker.value.remove()
      addressSearchMarker.value = null
    }

    // 取得した座標で地図を移動
    focusMapOnLocation(result.lat, result.lng, result.title)

    // マーカーを追加（mapInstanceが利用可能な場合）
    if (props.mapInstance) {
      // ポップアップを作成
      const popup = new maplibregl.Popup({
        offset: 25,
        closeButton: true,
        closeOnClick: false
      })
        .setHTML(`
          <div style="padding: 8px; min-width: 150px;">
            <strong>住所検索結果</strong><br>
            ${result.title}
          </div>
        `)

      // MapLibre GLのデフォルトマーカーを使用（赤色）
      addressSearchMarker.value = new maplibregl.Marker({
        color: '#dc3545'  // Bootstrap dangerカラー（赤色）
      })
        .setLngLat([result.lng, result.lat])
        .setPopup(popup)
        .addTo(props.mapInstance)
    }

    showToast(`「${result.title}」の位置を表示しました。\n出典：国土地理院ジオコーディングAPI\nhttps://msearch.gsi.go.jp/`, 'success')
  } catch (error) {
    console.error('住所検索エラー:', error)

    // エラーメッセージをユーザーに表示
    if (error.code === 'NO_RESULTS') {
      showToast(`住所「${address}」が見つかりませんでした。`, 'warning')
    } else if (error.code === 'NETWORK_ERROR') {
      showToast('ネットワークエラーが発生しました。', 'danger')
    } else {
      showToast(error.message || '住所検索中にエラーが発生しました。', 'danger')
    }
  }
}

/**
 * 管理番号で集積所を検索
 * @param {string} searchNumber - 検索する管理番号
 * @returns {Object|null} 見つかった集積所データまたはnull
 */
const findStationByNumber = (searchNumber) => {
  // stationsDataは$store.getters.stationData.stStatus配列なので、直接使用
  if (!props.stationsData || !Array.isArray(props.stationsData) || props.stationsData.length === 0) {
    return null
  }

  // stationsData（stStatus配列）から管理番号が一致する集積所を検索
  for (const station of props.stationsData) {
    if (!station || typeof station !== 'object') {
      continue
    }

    // stStatus配列の場合：stNameが管理番号、controlNumberは集積所名
    const managementNumber = station.stName || station.controlNumber || station.id || ''
    const name = station.controlNumber || `集積所 ${managementNumber}`
    const lat = station.latitude
    const lng = station.longitude

    // 管理番号を文字列として比較
    if (managementNumber.toString() === searchNumber.toString()) {
      const result = {
        id: managementNumber,
        managementNumber: managementNumber.toString(),
        name: name,
        lat: parseFloat(lat),
        lng: parseFloat(lng),
        originalData: station
      }

      return result
    }
  }

  return null
}

/**
 * 地図の中心を指定した集積所に移動
 * @param {Object} station - 集積所データ
 */
const focusMapOnStation = (station) => {
  if (!station || !station.lat || !station.lng) {
    console.warn('集積所の座標情報が不足しています:', station)
    return
  }

  // mapInstanceが利用可能な場合
  if (props.mapInstance && props.mapInstance.flyTo) {
    props.mapInstance.flyTo({
      center: [station.lng, station.lat],
      zoom: 18,
      duration: 1500
    })

    // ポップアップ表示のためにイベントを発行
    emit('focus-on-location', {
      lat: station.lat,
      lng: station.lng,
      zoom: 18,
      station: station
    })

  } else {
    // mapInstanceが利用できない場合は親コンポーネントに通知
    emit('focus-on-location', {
      lat: station.lat,
      lng: station.lng,
      zoom: 18,
      station: station
    })
  }
}

/**
 * 地図の中心を指定した座標に移動（住所検索用）
 * @param {number} lat - 緯度
 * @param {number} lng - 経度
 * @param {string} title - 場所の名前
 */
const focusMapOnLocation = (lat, lng, title) => {
  if (!lat || !lng) {
    console.warn('座標情報が不足しています:', { lat, lng })
    return
  }

  // mapInstanceが利用可能な場合
  if (props.mapInstance && props.mapInstance.flyTo) {
    props.mapInstance.flyTo({
      center: [lng, lat],
      zoom: 16, // 住所検索の場合は少し広めのズームレベル
      duration: 1500
    })

    // イベントを発行
    emit('focus-on-location', {
      lat: lat,
      lng: lng,
      zoom: 16,
      title: title
    })

  } else {
    // mapInstanceが利用できない場合は親コンポーネントに通知
    emit('focus-on-location', {
      lat: lat,
      lng: lng,
      zoom: 16,
      title: title
    })
  }
}

/**
 * トースト表示（ToastMessage.vueを使用）
 */
const showToast = (message, variant = 'info') => {
  toastRef.value?.showToast(message, variant)
}


onBeforeUnmount(() => {
  // マーカーのクリーンアップ
  if (addressSearchMarker.value) {
    addressSearchMarker.value.remove()
    addressSearchMarker.value = null
  }
})

// Vue 3 での配列監視を明示的に設定
watch(() => props.stationsData, (newVal, oldVal) => {
  // Vue 3での配列変更を適切に検知
  if (Array.isArray(newVal) && Array.isArray(oldVal)) {
    if (newVal.length !== oldVal.length || JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
      console.log('StationsData changed:', {
        newLength: newVal.length,
        oldLength: oldVal.length,
        isArray: true
      })
    }
  } else if (newVal !== oldVal) {
    console.log('StationsData initialized:', {
      length: Array.isArray(newVal) ? newVal.length : 0,
      isArray: Array.isArray(newVal)
    })
  }
}, {
  immediate: true,
  deep: true,
  flush: 'post' // Vue 3 で推奨される設定
})
</script>

<style scoped>
/* コンテナ固有のスタイルがあれば記述 */
</style>
